import React, { useContext, type PropsWithChildren } from 'react';
import type { IGoogleButtonRenderer } from './GoogleIdentityServicesSignIn.js';

const GoogleButtonRendererContext = React.createContext<IGoogleButtonRenderer | undefined>(undefined);

export interface GoogleButtonRendererProviderProps {
  renderer: IGoogleButtonRenderer;
}

/** Makes the renderer of Google's button available to the sign-in screen; `main.tsx` passes the Google adapter it built. */
export function GoogleButtonRendererProvider({ renderer, children }: PropsWithChildren<GoogleButtonRendererProviderProps>) {
  return <GoogleButtonRendererContext.Provider value={renderer}>{children}</GoogleButtonRendererContext.Provider>;
}

/**
 * Reads the renderer that draws Google's own "Continue with Google" button into a container.
 *
 * Takes no parameters: there is one renderer per page, provided once in `main.tsx`.
 *
 * @returns The `IGoogleButtonRenderer` given to the nearest `GoogleButtonRendererProvider`. It never changes while the
 * page lives, so it causes no re-renders.
 * @throws Error `useGoogleButtonRenderer must be used within GoogleButtonRendererProvider` when called outside a
 * `GoogleButtonRendererProvider`.
 */
export function useGoogleButtonRenderer(): IGoogleButtonRenderer {
  const renderer = useContext(GoogleButtonRendererContext);

  if (renderer === undefined) {
    throw new Error('useGoogleButtonRenderer must be used within GoogleButtonRendererProvider');
  }

  return renderer;
}
